import { connectRefinementList } from "instantsearch.js/es/connectors";
import { curatedFacetRefinementsSpacer, facetNoResultsLabel } from "../curated-layout";
import { curatedSearchConfig } from "@/services/algoliaRouting.blade";
import { escapeHtml, runAfterFrame } from "./helpers";

interface ModelFacetItem {
    value: string;
    label: string;
    count: number;
    isRefined: boolean;
}

const MODEL_FACET_CONTAINER_SELECTORS = [
    "#action-bar-model",
    "#refinement-model",
] as const;

function getModelFacetContainers(): HTMLElement[] {
    return MODEL_FACET_CONTAINER_SELECTORS.flatMap((selector) => {
        const el = document.querySelector<HTMLElement>(selector);
        return el ? [el] : [];
    });
}

function resolveSelectedModels(): Set<string> {
    const selected = new Set<string>();

    const curated = curatedSearchConfig.value.filters?.refinementList?.model;
    if (Array.isArray(curated)) {
        curated.forEach((value) => {
            if (value) selected.add(value);
        });
    }

    if (selected.size === 0) {
        const landingModels =
            window.__MARKETPLACE_CONFIG__?.landingData
                ?.concentratedModelDesignations ?? [];
        landingModels.forEach((value) => {
            if (value) selected.add(value);
        });
    }

    if (selected.size === 0) {
        (
            window.__MARKETPLACE_CONFIG__?.initialModelsFacet ?? []
        ).forEach((item) => {
            if (item.isRefined && item.value) {
                selected.add(item.value);
            }
        });
    }

    return selected;
}

/**
 * Model facet with SSR-frozen options (parity with createMakeMenuWidget).
 * Keeps landing hub + concentration satellites visible and pre-selected on first paint.
 */
export function createModelRefinementWidget() {
    let localSearchQuery = "";
    let listenersBound = false;
    let latestRefine: ((value: string) => void) | null = null;

    const scheduleFacetRender = (
        renderOptions: Parameters<
            Parameters<typeof connectRefinementList>[0]
        >[0],
    ) => {
        (
            renderOptions as typeof renderOptions & {
                instantSearchInstance: { scheduleRender: () => void };
            }
        ).instantSearchInstance.scheduleRender();
    };

    const frozenModels: ModelFacetItem[] = (
        window.__MARKETPLACE_CONFIG__?.initialModelsFacet ?? []
    ).map((item) => ({ ...item }));

    return connectRefinementList((renderOptions, isFirstRender) => {
        const containers = getModelFacetContainers();
        if (!containers.length) return;

        const {
            items: rawItems,
            refine,
            searchForItems,
            isShowingMore,
            canToggleShowMore,
            toggleShowMore,
        } = renderOptions;
        latestRefine = refine;

        const selectedModels = resolveSelectedModels();
        const liveByValue = new Map(rawItems.map((item) => [item.value, item]));

        const mergedItems: ModelFacetItem[] = frozenModels.length
            ? frozenModels.map((frozen) => {
                  const live = liveByValue.get(frozen.value);
                  return {
                      value: frozen.value,
                      label: frozen.label,
                      count: live?.count ?? frozen.count,
                      isRefined: selectedModels.has(frozen.value),
                  };
              })
            : rawItems.map((item) => ({
                  value: item.value,
                  label: item.label,
                  count: item.count,
                  isRefined: selectedModels.has(item.value),
              }));

        for (const live of rawItems) {
            if (mergedItems.some((item) => item.value === live.value)) {
                continue;
            }
            mergedItems.push({
                value: live.value,
                label: live.label,
                count: live.count,
                isRefined: selectedModels.has(live.value),
            });
        }

        const q = localSearchQuery.trim().toLowerCase();
        const displayItems = q
            ? mergedItems.filter((item) =>
                  item.label.toLowerCase().includes(q),
              )
            : mergedItems;
        const hasSelected = mergedItems.some((item) => item.isRefined);

        const listHtml = displayItems.length
            ? displayItems
                  .map(
                      (item) =>
                          `<label class="RefinementList${
                              item.isRefined
                                  ? " RefinementList--selected"
                                  : ""
                          }">` +
                          `<span><span class="RefinementList__labelText">${escapeHtml(
                              item.label,
                          )}</span>` +
                          `<span class="RefinementList__count">(${item.count.toLocaleString(
                              "fr-FR",
                          )})</span></span>` +
                          `<input type="checkbox" class="RefinementList__checkbox"` +
                          `${
                              item.isRefined ? " checked" : ""
                          } data-value="${escapeHtml(
                              item.value,
                          )}" /></label>`,
                  )
                  .join("")
            : `<span class="no-results-label">${facetNoResultsLabel()}</span>`;

        const showMoreHtml = canToggleShowMore
            ? `<button type="button" class="RefinementList__showMore">${
                  isShowingMore ? "Voir moins" : "Voir plus"
              }</button>`
            : "";

        const markup =
            `<div class="RefinementList__search input__field input__field--facette">` +
            `<input type="search" class="input input--search" placeholder="Rechercher un modèle…" autocomplete="off" data-model-facet-search /></div>` +
            curatedFacetRefinementsSpacer("model") +
            `<div class="RefinementList__wrapper${
                hasSelected ? " RefinementList__wrapper--has-selected" : ""
            }">${listHtml}${showMoreHtml}</div>`;

        for (const container of containers) {
            if (isFirstRender) {
                container.innerHTML = markup;
            } else {
                const wrap = container.querySelector<HTMLElement>(
                    ".RefinementList__wrapper",
                );
                if (wrap) {
                    wrap.classList.toggle(
                        "RefinementList__wrapper--has-selected",
                        hasSelected,
                    );
                    wrap.innerHTML = listHtml + showMoreHtml;
                }
            }
        }

        if (!listenersBound && isFirstRender) {
            listenersBound = true;

            // Bind search on every mount (sidebar + action-bar). Listening only
            // on containers[0] left the action-bar input inert when it was the
            // secondary selector — which is the one users type in.
            const onModelSearchInput = (event: Event) => {
                const input = event.target as HTMLInputElement;
                if (
                    !(input instanceof HTMLInputElement) ||
                    !input.matches("[data-model-facet-search]")
                ) {
                    return;
                }
                localSearchQuery = input.value;
                containers.forEach((container) => {
                    const mirror = container.querySelector<HTMLInputElement>(
                        "[data-model-facet-search]",
                    );
                    if (mirror && mirror !== input) {
                        mirror.value = input.value;
                    }
                });
                searchForItems(localSearchQuery);
                // Re-run the connector so `displayItems` client filter applies
                // immediately; `searchForItems` alone may not re-render until
                // the facet API responds (and SSR-frozen items stay unfiltered).
                scheduleFacetRender(renderOptions);
            };

            containers.forEach((container) => {
                container.addEventListener("input", onModelSearchInput);
            });

            // Same event split as createMenuStyleRefinementWidget: refine on
            // `change` only, deferred past the next paint (INP). A `click`
            // refine would double-fire now that the input survives the tap.
            const onShowMoreClick = (event: Event) => {
                const target = event.target as HTMLElement;
                if (target.classList.contains("RefinementList__showMore")) {
                    runAfterFrame(() => toggleShowMore());
                }
            };
            const onCheckboxChange = (event: Event) => {
                const input = event.target as HTMLInputElement;
                if (!input.classList.contains("RefinementList__checkbox")) {
                    return;
                }
                const val = input.dataset.value;
                if (val) runAfterFrame(() => latestRefine?.(val));
            };

            containers.forEach((container) => {
                container.addEventListener("change", onCheckboxChange);
                container.addEventListener("click", onShowMoreClick);
            });
        }
    })({
        attribute: "model",
        operator: "or",
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ["isRefined", "count:desc"],
    });
}
